import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { getJlptLevel } from "../utils/jlptLevelStorage";
import { JlptLevel, normaliseJlptLevels } from "../utils/jlptLevelPreferences";
import { ProgressSyncStatus } from "./progressTypes";

const FEATURE_KEY = "jlptLevels";

function readLevels(data: unknown): JlptLevel[] | null {
    if (!data || typeof data !== "object" || !Array.isArray((data as { levels?: unknown }).levels)) return null;
    return normaliseJlptLevels((data as { levels: unknown }).levels);
}

// Preferred JLPT ranges are account data. The old single-level preference is
// used once as a migration seed so returning learners retain their choice.
export function useJlptLevels() {
    const { status, user } = useAuth();
    const [levels, setLevelsState] = useState<JlptLevel[]>(() => normaliseJlptLevels([getJlptLevel() || "5"]));
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");

    useEffect(() => {
        if (status !== "authenticated" || !user?.identityId) {
            setLevelsState(["5"]);
            setSyncStatus(status === "unauthenticated" ? "signedOut" : "local");
            return undefined;
        }

        let cancelled = false;
        const fallback = normaliseJlptLevels([getJlptLevel() || "5"]);
        setLevelsState(fallback);
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                const stored = readLevels(remote?.data);
                const next = stored || fallback;
                setLevelsState(next);
                setSyncStatus("ready");
                // Establish the account record on first use, including users
                // migrating from the old single-level setting.
                if (!stored) putCloudProgress(user.identityId!, FEATURE_KEY, { levels: next }).catch(() => undefined);
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => { cancelled = true; };
    }, [status, user?.identityId]);

    const setLevels = useCallback((next: unknown) => {
        const validLevels = normaliseJlptLevels(next);
        setLevelsState(validLevels);
        if (status === "authenticated" && user?.identityId) {
            setSyncStatus("ready");
            putCloudProgress(user.identityId, FEATURE_KEY, { levels: validLevels }).catch(() => setSyncStatus("error"));
        }
    }, [status, user?.identityId]);

    return { levels, setLevels, syncStatus };
}
